import React, { useEffect } from 'react';
import { SelectField } from '@flos/react-ui';
import useAppState from '../../state/appState';
import { ElementValue } from '../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import {
    TimeIntervals as TimeIntervalType,
    FormValues,
    FormConfig
} from '@topdanmark-onlinesolutions/claim-form-types';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { isRequired } from '../../helpers/isRequired';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getValue } from '../values/Get';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

const TimeIntervals = ({ elementId, caption, placeholder, helpText, startHour, endHour }: TimeIntervalType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const value = TimeIntervals.getValueOrDefault(formValues, elementId);

    const [errorMessage, setErrorMessage] = React.useState('');

    type Options = {
        text: string;
        value: string;
    };

    const [timeList, setTimeList] = React.useState<Options[]>([]);

    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = React.useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    useEffect(() => {
        const timeArray = [
            { start: '00:00', end: '00:59' },
            { start: '01:00', end: '01:59' },
            { start: '02:00', end: '02:59' },
            { start: '03:00', end: '03:59' },
            { start: '04:00', end: '04:59' },
            { start: '05:00', end: '05:59' },
            { start: '06:00', end: '06:59' },
            { start: '07:00', end: '07:59' },
            { start: '08:00', end: '08:59' },
            { start: '09:00', end: '09:59' },
            { start: '10:00', end: '10:59' },
            { start: '11:00', end: '11:59' },
            { start: '12:00', end: '12:59' },
            { start: '13:00', end: '13:59' },
            { start: '14:00', end: '14:59' },
            { start: '15:00', end: '15:59' },
            { start: '16:00', end: '16:59' },
            { start: '17:00', end: '17:59' },
            { start: '18:00', end: '18:59' },
            { start: '19:00', end: '19:59' },
            { start: '20:00', end: '20:59' },
            { start: '21:00', end: '21:59' },
            { start: '22:00', end: '22:59' },
            { start: '23:00', end: '23:59' }
        ];

        const timeUnits: Options[] = [];

        const split = startHour > endHour ? 23 : endHour;

        timeUnits.push({ text: 'Vælg...', value: 'select' });

        for (let i = startHour; i <= split; i++) {
            const timestring = `${timeArray[i].start} - ${timeArray[i].end}`;
            timeUnits.push({ text: timestring, value: timestring });
        }

        // If time passed midnight
        if (split === 23) {
            for (let i = 0; i <= endHour; i++) {
                const timestring = `${timeArray[i].start} - ${timeArray[i].end}`;
                timeUnits.push({ text: timestring, value: timestring });
            }
        }
        setTimeList(timeUnits);
    }, [startHour, endHour]);

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [value]);

    const isRequiredField = isRequired(formConfig, elementId);

    const isValid = TimeIntervals.isValidField(formConfig, formValues, elementId, fieldHasHadFocusAtLeastOnce);

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <SelectField
                required={isRequiredField}
                label={placeholder}
                id={elementId}
                key={elementId}
                options={timeList.map((option) => ({
                    label: option.text,
                    value: option.value
                }))}
                isValid={isValid}
                helpText={helpText}
                errorText={errorMessage}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    const value = event.target.value;
                    setFormValue(elementId, value);
                    setFieldHasHadFocusAtLeastOnce(true);
                }}
                value={value}
            />
        </>
    );
};

TimeIntervals.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as TimeIntervalType;
    const { summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={value} />;
};

TimeIntervals.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    label: string
): ClaimFormSubmitFieldValue => {
    const value = TimeIntervals.getValue(formValues, elementId);
    const valueStr = TimeIntervals.getValueHuman(formValues, elementId);

    const valueId = { string: value };

    return getClaimFormSubmitValue('timeintervals', elementId, label, valueStr, valueId);
};

TimeIntervals.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

TimeIntervals.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = TimeIntervals.getValue(formValues, elementId);
    if (value === undefined) {
        return '';
    }
    return value;
};

TimeIntervals.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const value = TimeIntervals.getValue(formValues, elementId);
    return value;
};

TimeIntervals.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    return getIsValid(formConfig, formValues, elementId);
};

TimeIntervals.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHasHadFocusAtLeastOnce) {
        return undefined;
    }

    const isValid = TimeIntervals.isValid(formConfig, formValues, elementId);

    return isValid;
};

export default TimeIntervals;
